package listening

import AttemptDetail
import ReadingQuestion
import ReadingSet
import UserProgress
import androidx.compose.runtime.*
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch
import supabase
import kotlin.collections.emptyList
import kotlin.collections.get

@Composable
fun ListeningTask1Screen(
    taskId: Int, // Передаем 11
    onExit: () -> Unit
) {
    // --- State ---
    var currentSet by remember { mutableStateOf<ReadingSet?>(null) }
    var correctAnswers by remember { mutableStateOf<List<ReadingQuestion>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    val startTime = remember { window.performance.now() }

    // Ответы пользователя
    var userAnswers by remember { mutableStateOf<Map<Int, String>>(emptyMap()) }
    var isFinished by remember { mutableStateOf(false) }

    // Активный пропуск
    var activeGapIndex by remember { mutableStateOf<Int?>(null) }

    // Scope для корутин
    val scope = rememberCoroutineScope()

    // --- Загрузка ---
    LaunchedEffect(taskId) {
        isLoading = true
        try {
            val currentUser = supabase.auth.currentUserOrNull()

            // 1. Узнаем, какие сеты пользователь уже решил
            val solvedSetIds = if (currentUser != null) {
                supabase.from("user_progress").select {
                    filter {
                        eq("user_id", currentUser.id)
                        eq("task_id", taskId)
                    }
                }.decodeList<UserProgress>().mapNotNull { it.setId }
            } else {
                emptyList()
            }

            // 2. Загружаем ВСЕ сеты для этого типа задач
            val allSets = supabase.from("reading_sets").select {
                filter { eq("task_type", taskId) }
            }.decodeList<ReadingSet>()

            // 3. Фильтруем: берем первый, которого НЕТ в решенных
            val targetSet = allSets.firstOrNull { it.id !in solvedSetIds }

            currentSet = targetSet

            // 4. Если нашли новый сет, загружаем для него правильные ответы
            if (targetSet != null) {
                correctAnswers = supabase.from("reading_items").select {
                    filter { eq("set_id", targetSet.id) }
                    order("order_index", Order.ASCENDING)
                }.decodeList<ReadingQuestion>()
            }
        } catch (e: Exception) {
            console.error("Error loading task: ${e.message}")
        } finally {
            isLoading = false
        }
    }

    // --- Сохранение ---
    fun finishTask() {
        isFinished = true
        activeGapIndex = null
        val score = correctAnswers.count { q ->
            val index = q.orderIndex - 1
            userAnswers[index] == q.correctOption
        }
// 2. СЧИТАЕМ ВРЕМЯ
        val endTime = window.performance.now()
        val durationSeconds = ((endTime - startTime) / 1000).toInt()

        // 3. ФОРМИРУЕМ ДЕТАЛИ
        // Для Tarea 1 вопросы - это пропуски. Текст вопроса берем из контекста или пишем "Espacio X"
        val detailsList = correctAnswers.map { q ->
            val gapIndex = q.orderIndex - 1
            val userAnsId = userAnswers[gapIndex]

            // Находим текст опции по ID (например "terraza")
            val userText = currentSet?.optionsPool?.find { it.id == userAnsId }?.text ?: "---"
            val correctText = currentSet?.optionsPool?.find { it.id == q.correctOption }?.text ?: "???"

            AttemptDetail(
                question = "Espacio ${q.orderIndex}",
                userAnswer = userText,
                correctAnswer = correctText,
                isCorrect = userAnsId == q.correctOption
            )
        }

        currentSet?.let { set->
            scope.launch {
                DataRepository.saveProgress(taskId, set.id, score, correctAnswers.size)
                DataRepository.saveAttempt(
                    taskId = taskId,
                    setTitle = set.title,
                    score = score,
                    total = correctAnswers.size,
                    durationSeconds = durationSeconds,
                    details = detailsList
                )
            }
        }
    }

    Div({
        style {
            padding(20.px); maxWidth(1000.px);
            property("margin-left", "auto")
            property("margin-right", "auto")
            fontFamily("Arial", "sans-serif")
        }
    }) {
        // 1. Идет загрузка
        if (isLoading) {
            H3 { Text("⏳ Cargando audio...") }
            return@Div
        }

        // 2. Если загрузка прошла, но задач нет (все решены)
        if (currentSet == null) {
            Div({ style { textAlign("center"); marginTop(50.px) } }) {
                H2 { Text("🎉 ¡Todo completado!") }
                P { Text("Has resuelto todos los audios disponibles para esta tarea.") }
                Button({
                    onClick { onExit() }
                    style {
                        marginTop(20.px); padding(10.px, 20.px); cursor("pointer")
                        backgroundColor(Color("#3498db")); color(Color.white); border(0.px); borderRadius(5.px)
                    }
                }) { Text("Volver al menú") }
            }
            return@Div
        }

        // 3. Отображение задачи
        val set = currentSet!!
        val parts = set.fullText?.split("{{gap}}") ?: emptyList()

        // КНОПКА ВЫХОДА
        Button({ onClick { onExit() }; style { marginBottom(15.px); cursor("pointer") } }) { Text("⬅ Salir") }

        // АУДИО ПЛЕЕР
        if (set.audioUrl != null) {
            Audio({
                attr("controls", "")
                style { width(100.percent); marginBottom(30.px) }
            }) {
                Source({ attr("src", set.audioUrl) })
            }
        }

        // ТЕКСТ С ПРОПУСКАМИ
        Div({
            style {
                backgroundColor(Color.white); padding(30.px); borderRadius(12.px)
                property("box-shadow", "0 4px 10px rgba(0,0,0,0.05)")
                lineHeight("2.5em")
                fontSize(1.2.cssRem)
            }
        }) {
            H3 { Text(set.title) }

            parts.forEachIndexed { index, textPart ->
                Span { Text(textPart) }

                if (index < parts.size - 1) {
                    val gapIndex = index
                    val selectedOptionId = userAnswers[gapIndex]
                    val selectedText =
                        set.optionsPool?.find { it.id == selectedOptionId }?.text ?: "Espacio ${gapIndex + 1}"

                    val isCorrect =
                        if (isFinished) correctAnswers.getOrNull(gapIndex)?.correctOption == selectedOptionId else false

                    Button({
                        onClick { if (!isFinished) activeGapIndex = gapIndex }
                        style {
                            marginLeft(5.px); marginRight(5.px)
                            padding(5.px, 15.px)
                            borderRadius(20.px)
                            cursor(if (isFinished) "default" else "pointer")
                            border(1.px, LineStyle.Solid, Color("#bdc3c7"))

                            if (isFinished) {
                                if (isCorrect) {
                                    backgroundColor(Color("#2ecc71")); color(Color.white)
                                } else {
                                    backgroundColor(Color("#e74c3c")); color(Color.white)
                                }
                            } else if (activeGapIndex == gapIndex) {
                                backgroundColor(Color("#3498db")); color(Color.white)
                            } else if (selectedOptionId != null) {
                                backgroundColor(Color("#d6eaf8")); color(Color("#2980b9"))
                            } else {
                                backgroundColor(Color("#f0f3f4")); color(Color("#7f8c8d"))
                            }
                        }
                    }) {
                        Text(if (isFinished && selectedOptionId == null) "---" else selectedText)
                    }
                }
            }
        }

        // ПАНЕЛЬ ВЫБОРА
        if (activeGapIndex != null && !isFinished) {
            Div({
                style {
                    marginTop(20.px); padding(20.px); backgroundColor(Color("#ecf0f1"))
                    borderRadius(12.px); border(1.px, LineStyle.Solid, Color("#bdc3c7"))
                }
            }) {
                Div({ style { marginBottom(10.px); fontWeight("bold"); color(Color("#2c3e50")) } }) {
                    Text("Selecciona una opción para el espacio ${activeGapIndex!! + 1}:")
                }

                Div({
                    style { display(DisplayStyle.Flex); property("flex-wrap", "wrap"); property("gap", "10px") }
                }) {
                    set.optionsPool?.forEach { option ->
                        val isUsedElsewhere =
                            userAnswers.containsValue(option.id) && userAnswers[activeGapIndex] != option.id

                        Button({
                            if (!isUsedElsewhere) {
                                onClick {
                                    userAnswers = userAnswers + (activeGapIndex!! to option.id)
                                    activeGapIndex = null
                                }
                            }
                            style {
                                padding(10.px, 15.px)
                                borderRadius(8.px); border(0.px); cursor("pointer")
                                fontSize(1.cssRem)

                                if (userAnswers[activeGapIndex] == option.id) {
                                    backgroundColor(Color("#3498db")); color(Color.white)
                                } else if (isUsedElsewhere) {
                                    backgroundColor(Color("#e0e0e0")); color(Color("#bdc3c7")); textDecoration("line-through")
                                } else {
                                    backgroundColor(Color.white); color(Color("#2c3e50"))
                                    property("box-shadow", "0 2px 5px rgba(0,0,0,0.1)")
                                }
                            }
                        }) {
                            Span({ style { fontWeight("bold"); marginRight(5.px) } }) { Text("(${option.id})") }
                            Text(option.text)
                        }
                    }
                }
            }
        }

        // КНОПКА ЗАВЕРШИТЬ
        if (!isFinished) {
            Button({
                style {
                    marginTop(30.px); width(100.percent); padding(15.px)
                    backgroundColor(Color("#2c3e50")); color(Color.white)
                    border(0.px); borderRadius(8.px); fontSize(1.2.cssRem); cursor("pointer")
                }
                onClick {

                    finishTask()
                }
            }) {
                Text("Finalizar Auditiva")
            }
        } else {
            val score = correctAnswers.count { q -> userAnswers[q.orderIndex - 1] == q.correctOption }
            Div({ style { marginTop(20.px); textAlign("center"); fontSize(1.5.cssRem) } }) {
                Text("Resultado: $score / ${correctAnswers.size}")
            }
        }
    }
}